import React, { createContext, useContext } from 'react';

/**
 * Tabela de dados com tokens de tema. Compõe-se como a `<table>` nativa:
 *
 * ```tsx
 * <TableContainer>
 *   <Table>
 *     <Thead><Tr><Th>Nome</Th><Th align="right">Valor</Th></Tr></Thead>
 *     <Tbody>
 *       {itens.map(i => <Tr key={i.id}><Td>{i.nome}</Td><Td align="right">{i.valor}</Td></Tr>)}
 *       {itens.length === 0 && <TableEmpty colSpan={2}>Nenhum item.</TableEmpty>}
 *     </Tbody>
 *   </Table>
 * </TableContainer>
 * ```
 */

/** Borda, raio e rolagem horizontal (tabela larga não estoura a página no celular). */
export const TableContainer: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...rest }) => (
  <div className={`overflow-x-auto rounded-panel border border-line bg-surface ${className}`} {...rest} />
);

/** Espaçamento das células: `compact` para muitas colunas, `comfortable` para poucas colunas e linhas de respiro. */
export type TableDensity = 'compact' | 'default' | 'comfortable';

const CELL_PADDING: Record<TableDensity, string> = {
  compact: 'px-3 py-2',
  default: 'px-4 py-3',
  comfortable: 'px-5 py-4',
};

const DensityContext = createContext<TableDensity>('default');

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  density?: TableDensity;
}

export const Table: React.FC<TableProps> = ({ density = 'default', className = '', ...rest }) => (
  <DensityContext.Provider value={density}>
    <table className={`min-w-full text-sm text-fg ${className}`} {...rest} />
  </DensityContext.Provider>
);

export const Thead: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className = '', ...rest }) => (
  <thead className={`border-b border-line bg-surface-muted ${className}`} {...rest} />
);

export const Tbody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className = '', ...rest }) => (
  <tbody className={`divide-y divide-line ${className}`} {...rest} />
);

type Align = 'left' | 'right' | 'center';
const ALIGN: Record<Align, string> = { left: 'text-left', right: 'text-right', center: 'text-center' };

interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
}

export const Th: React.FC<ThProps> = ({ align = 'left', className = '', scope = 'col', ...rest }) => (
  <th
    scope={scope}
    className={`${CELL_PADDING[useContext(DensityContext)]} text-xs font-semibold uppercase tracking-wide text-fg-muted ${ALIGN[align]} ${className}`}
    {...rest}
  />
);

interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  align?: Align;
}

export const Td: React.FC<TdProps> = ({ align = 'left', className = '', ...rest }) => (
  <td className={`${CELL_PADDING[useContext(DensityContext)]} align-top ${ALIGN[align]} ${className}`} {...rest} />
);

/** Linha. Com `onClick`, vira clicável (cursor e hover); sem, fica estática. */
export const Tr: React.FC<React.HTMLAttributes<HTMLTableRowElement>> = ({ className = '', onClick, ...rest }) => (
  <tr
    onClick={onClick}
    className={`${onClick ? 'cursor-pointer hover:bg-surface-muted' : ''} ${className}`}
    {...rest}
  />
);

/** Linha única para "sem registros" / "carregando", ocupando todas as colunas. */
export const TableEmpty: React.FC<{ colSpan: number; children: React.ReactNode }> = ({ colSpan, children }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-fg-muted">
      {children}
    </td>
  </tr>
);
